import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { createAppTheme } from '../theme';

const ThemeContext = createContext();

// Helper: read system preference
const getSystemTheme = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }) => {
  // 'light' | 'dark' | 'system'  — what the user chose
  const [themePreference, setThemePreferenceRaw] = useState(() => {
    try {
      const saved = localStorage.getItem('theme-mode');
      if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
      return 'system'; // default to system
    } catch {
      return 'system';
    }
  });

  // Resolved system theme (used only when preference === 'system')
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  // Listen for OS theme changes
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Resolved theme mode: always 'light' or 'dark'
  const themeMode = themePreference === 'system' ? systemTheme : themePreference;

  // Memoize theme creation to prevent unnecessary recreations
  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  // Add hydration check to ensure theme is consistent after SSR/initial render
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Set preference and persist
  const setThemePreference = useCallback((pref) => {
    setThemePreferenceRaw(pref);
    try {
      localStorage.setItem('theme-mode', pref);
    } catch (error) {
      console.warn('Failed to save theme preference:', error);
    }
  }, []);

  // Cycle: system → light → dark → system
  const toggleTheme = useCallback(() => {
    const next = themePreference === 'system' ? 'light'
               : themePreference === 'light'  ? 'dark'
               : 'system';
    setThemePreference(next);
  }, [themePreference, setThemePreference]);

  const value = {
    themeMode,           // resolved: 'light' | 'dark'
    themePreference,     // user choice: 'light' | 'dark' | 'system'
    toggleTheme,
    setThemePreference,
    isDark: themeMode === 'dark',
    isLight: themeMode === 'light',
    isHydrated,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
